import { useCallback, useEffect, useState } from "react"

/** How far the collapsing body may shrink and still show the lesson title. */
const FLOOR_PROPERTY = "--collapsed-floor"

/**
 * Keeps `--collapsed-floor` on the collapsing body equal to the distance from its top down to
 * the bottom of the lesson title, so collapsing stops there instead of at zero.
 *
 * It has to be measured: the title wraps (a two-line title needs a taller floor), and CSS has
 * no way to say "no shorter than my first child". The title is found by the same
 * `data-content-type="lessonTitle"` hook the post-drop scroll watcher uses — it is on the live
 * node view, which ProseMirror recreates on doc changes, hence the MutationObserver.
 */
export function useLessonTitleFloor(): (element: HTMLDivElement | null) => void {
  const [body, setBody] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!body) return

    let title: Element | null = null

    const measure = () => {
      if (!title) return
      const floor = title.getBoundingClientRect().bottom - body.getBoundingClientRect().top
      body.style.setProperty(FLOOR_PROPERTY, `${Math.max(0, Math.round(floor))}px`)
    }

    // Re-observe whenever ProseMirror swaps the node view for a new element.
    const sizes = new ResizeObserver(measure)
    const findTitle = () => {
      const found = body.querySelector('[data-content-type="lessonTitle"]')
      if (found === title) return
      title = found
      sizes.disconnect()
      if (title) {
        sizes.observe(title)
        measure()
      }
    }

    // Re-measure on every change under the body, not just when the node view is replaced: the
    // floor is a distance from the body's top, so anything that moves the title down — the
    // lesson's comments arriving above it, a card added while collapsed — invalidates it, and a
    // ResizeObserver on the title alone never sees that (its own size didn't change).
    const children = new MutationObserver(() => {
      findTitle()
      measure()
    })
    children.observe(body, { childList: true, subtree: true })
    findTitle()

    return () => {
      children.disconnect()
      sizes.disconnect()
    }
  }, [body])

  return useCallback((element: HTMLDivElement | null) => setBody(element), [])
}
